import { NotificationAlertWarningShieldSolidIcon } from '@tableau/icons/NotificationAlertWarningShieldSolidIcon';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import {
  ButtonType,
  ButtonWidget,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  DropdownButtonType,
  ListItem,
  LocallyFilteringList,
  TextLinkWidget,
  Tooltip,
} from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { FormikSingleSelectDropdown } from '~src/commonInfra/components/FormikSingleSelectDropdown';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { SelectModelsDialogStyles } from '~src/settings/general-settings/styles/tableau-gai/SelectModelsDialogStyles';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';

import { Messages } from '../../../../../codegen/Localize';
import { DEFAULT_MODELS } from './GenerativeAITypes';

export interface SelectModelsDialogProps {
  onClose: () => void;
  onSubmit: (values: ModelSelection) => void;
  modelQueryParsingOptions: any[];
  modelVizAndCalcOptions: any[];
  modelEmbeddingsOptions: any[];
  shouldShowAutomaticModelValidation: boolean;
  isDefaultQueryParsingModelAvailable: boolean;
  isDefaultVizAndCalcModelAvailable: boolean;
  isDefaultEmbeddingsModelAvailable: boolean;
  initialValues: ModelSelection;
  enablesFeatureOnApply: boolean;
  isOpenAIProvider: boolean;
}

export interface ModelSelection {
  modelQueryParsing: string;
  modelVizAndCalcGeneration: string;
  modelEmbeddings: string;
}

type ListItemWithDescription = ListItem & { description?: string };

function DetailedItemContent({ value, description }: ListItemWithDescription): React.ReactElement {
  return (
    <div style={{ marginTop: 8, marginBottom: 8, whiteSpace: 'normal', textAlign: 'unset' }}>
      <div style={{ marginBottom: 4, fontWeight: 'bold' }}>{value}</div>
      {description && <div>{description}</div>}
    </div>
  );
}

function buildModelOptionsWithDefaults(defaultModel: string, availableModels: string[]): any[] {
  const items: ListItemWithDescription[] = [];
  const isDefaultAvailable = availableModels.includes(defaultModel);

  items.push({
    value: defaultModel,
    description: Messages.selectModelsDialogAutomaticDescription(),
    disabled: !isDefaultAvailable,
  });

  items.push({ separator: true } as any);

  const remainingModels = availableModels.filter((model) => model !== defaultModel);
  remainingModels.forEach((model) => {
    items.push({ value: model });
  });

  return items.map((item) => {
    if (item.separator) {
      return item;
    }
    if (item.description) {
      return {
        ...item,
        content: <DetailedItemContent value={`${Messages.selectModelsDialogAutomatic()} (${item.value})`} description={item.description} />,
      };
    }
    return { ...item, content: item.value };
  });
}

export function buildSelectModelsDialogProps(
  isOpenAIProvider: boolean,
  models: string[],
  initialQueryParsingModel: string,
  initialVizAndCalcGenerationModel: string,
  initialEmbeddingsModel: string,
  onSubmit: (values: ModelSelection) => void,
  onClose: () => void,
  enablesFeatureOnApply: boolean
): SelectModelsDialogProps {
  // Sort models alphabetically and optionally add "Select a model and version" at the beginning
  // Only exclude the default option if provider is OpenAI
  const sortedModels = isOpenAIProvider ? models.sort() : [Messages.selectModelsDialogDefaultOption()].concat(models.sort());

  const baseModelOptions = sortedModels.map((model) => ({ value: model, content: model }));
  const modelQueryParsingOptions = isOpenAIProvider
    ? buildModelOptionsWithDefaults(DEFAULT_MODELS.QUERY_PARSING, sortedModels)
    : baseModelOptions;
  const modelVizAndCalcOptions = isOpenAIProvider
    ? buildModelOptionsWithDefaults(DEFAULT_MODELS.VIZ_AND_CALC, sortedModels)
    : baseModelOptions;
  const modelEmbeddingsOptions = isOpenAIProvider
    ? buildModelOptionsWithDefaults(DEFAULT_MODELS.EMBEDDINGS, sortedModels)
    : baseModelOptions;

  return {
    onClose: onClose,
    onSubmit: onSubmit,
    modelQueryParsingOptions: modelQueryParsingOptions,
    modelVizAndCalcOptions: modelVizAndCalcOptions,
    modelEmbeddingsOptions: modelEmbeddingsOptions,
    shouldShowAutomaticModelValidation: isOpenAIProvider,
    isDefaultQueryParsingModelAvailable: sortedModels.includes(DEFAULT_MODELS.QUERY_PARSING),
    isDefaultVizAndCalcModelAvailable: sortedModels.includes(DEFAULT_MODELS.VIZ_AND_CALC),
    isDefaultEmbeddingsModelAvailable: sortedModels.includes(DEFAULT_MODELS.EMBEDDINGS),
    enablesFeatureOnApply: enablesFeatureOnApply,
    isOpenAIProvider: isOpenAIProvider,
    initialValues: {
      modelQueryParsing: initialQueryParsingModel,
      modelVizAndCalcGeneration: initialVizAndCalcGenerationModel,
      modelEmbeddings: initialEmbeddingsModel,
    },
  };
}

function getButtonDisplayText(selectedModel: string, defaultModel: string, shouldShowAutomaticModelValidation: boolean): string {
  if (!selectedModel) {
    return `${Messages.selectModelsDialogDefaultOption()}`;
  }
  if (shouldShowAutomaticModelValidation && selectedModel === defaultModel) {
    return `${Messages.selectModelsDialogAutomatic()} (${selectedModel})`;
  }
  return selectedModel;
}

function getDropdownValidationState(
  selectedModel: string,
  isDefaultModelAvailable: boolean,
  defaultModel: string,
  shouldShowAutomaticModelValidation: boolean,
  options: any[]
): { message: string | JSX.Element | undefined, validSelection: boolean | undefined } {
  // If the current selection isn't in the provider's available list, surface
  // a "no longer available" error regardless of whether automatic-validation
  // is enabled — a stale default or a removed model both fail this check.
  if (selectedModel && !isSelectionAvailable(selectedModel, options)) {
    const isAutomatic = shouldShowAutomaticModelValidation && selectedModel === defaultModel;
    if (isAutomatic && !isDefaultModelAvailable) {
      return {
        message: Messages.selectModelsDialogAutomaticUnavailableError(),
        validSelection: false,
      };
    }
    return {
      message: Messages.selectModelsDialogModelUnavailableError(),
      validSelection: false,
    };
  }

  if (!shouldShowAutomaticModelValidation) {
    return { message: undefined, validSelection: undefined };
  }

  const isAutomatic = selectedModel === defaultModel;

  if (!isAutomatic) {
    return {
      message: (
        <span className={SelectModelsDialogStyles.warningMessageContainer}>
          <span className={SelectModelsDialogStyles.warningIcon}>
            <NotificationAlertWarningShieldSolidIcon size={14} />
          </span>
          <span>{Messages.selectModelsDialogNonAutomaticWarning()}</span>
        </span>
      ),
      validSelection: undefined,
    };
  }

  return { message: undefined, validSelection: undefined };
}

function ModelLabelSection({ htmlFor, label, tooltip }: { htmlFor: string, label: string, tooltip: string }): JSX.Element {
  return (
    <tr>
      <td className="tb-text-input-label-cell tb-wide-overflow">
        <div className={SelectModelsDialogStyles.labelWithTooltipContainer}>
          <label htmlFor={htmlFor}>{label}</label>
          <Tooltip tooltipContent={tooltip}>
            <NotificationInfoOutlineIcon size={16} />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function ModelDropdownSection({
  name,
  options,
  value,
  defaultModel,
  isDefaultAvailable,
  shouldShowAutomaticModelValidation,
  testId,
}: {
  name: string;
  options: any[];
  value: string;
  defaultModel: string;
  isDefaultAvailable: boolean;
  shouldShowAutomaticModelValidation: boolean;
  testId: string;
}): JSX.Element {
  const { message, validSelection } = getDropdownValidationState(
    value,
    isDefaultAvailable,
    defaultModel,
    shouldShowAutomaticModelValidation,
    options
  );

  return (
    <tr>
      <td className="tb-settings-control tb-input-wide">
        <FormikSingleSelectDropdown
          name={name}
          list={<LocallyFilteringList items={options} className={SelectModelsDialogStyles.dropdownListStyle} />}
          type={DropdownButtonType.Outline}
          label={''}
          className={SelectModelsDialogStyles.dropdownStyle}
          testId={testId}
          value={value}
          valid={validSelection}
          message={message}
        >
          {getButtonDisplayText(value, defaultModel, shouldShowAutomaticModelValidation)}
        </FormikSingleSelectDropdown>
      </td>
    </tr>
  );
}

function ModelTextFieldSection({ name, testId }: { name: string, testId: string }): JSX.Element {
  return (
    <tr>
      <td className="tb-settings-control tb-input-wide">
        <FormikTextBox
          name={name}
          testId={testId}
          label={''}
          placeholder={Messages.selectModelsDialogModelNamePlaceholder()}
          className={SelectModelsDialogStyles.dropdownStyle}
        />
      </td>
    </tr>
  );
}

function isSelectionAvailable(selectedModel: string, options: any[]): boolean {
  const match = options.find((opt) => !opt.separator && opt.value === selectedModel);
  return !!match && !match.disabled;
}

function isSubmitDisabled(formik: FormikProps<ModelSelection>, props: SelectModelsDialogProps): boolean {
  const defaultOption = Messages.selectModelsDialogDefaultOption();
  const baseDisabled =
    !formik.isValid ||
    formik.isSubmitting ||
    !formik.values.modelQueryParsing ||
    !formik.values.modelVizAndCalcGeneration ||
    !formik.values.modelEmbeddings ||
    formik.values.modelQueryParsing === defaultOption ||
    formik.values.modelVizAndCalcGeneration === defaultOption ||
    formik.values.modelEmbeddings === defaultOption;
  if (baseDisabled) {
    return true;
  }
  // For OpenAI we have a known list of available models from the provider — block
  // apply if any selection (including a stale default) isn't in that list. BYOLLM
  // and Azure use free-text fields, so there's no list to validate against.
  if (!props.isOpenAIProvider) {
    return false;
  }
  return (
    !isSelectionAvailable(formik.values.modelQueryParsing, props.modelQueryParsingOptions) ||
    !isSelectionAvailable(formik.values.modelVizAndCalcGeneration, props.modelVizAndCalcOptions) ||
    !isSelectionAvailable(formik.values.modelEmbeddings, props.modelEmbeddingsOptions)
  );
}

export function SelectModelsDialog(props: SelectModelsDialogProps): JSX.Element {
  const initialValues = props.initialValues;
  const selectModelsLearnMoreLink = HelpService.getHelpUrlByCategory('gpt-server-models');

  const handleSubmit = async (values: ModelSelection, helpers: FormikHelpers<ModelSelection>) => {
    helpers.setSubmitting(true);
    props.onSubmit(values);
    helpers.setSubmitting(false);
    props.onClose();
  };

  return (
    <DialogWidget
      testId="select-models-dialog"
      type={DialogType.NormalWithTitleBar}
      title={Messages.selectModelsDialogTitle()}
      showCloseButton={true}
      onCloseRequest={props.onClose}
      handleGlassClick={props.onClose}
      positioningBehavior={DialogPositioningBehavior.Centered}
      isModal={true}
      shouldAdjustPositionOnWindowResize={true}
      dialogWidth={TabDialogWidth.width400px}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={(values: ModelSelection, formik: FormikHelpers<ModelSelection>) => {
          handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<ModelSelection>) => (
          <form onSubmit={formik.handleSubmit} data-tb-test-id="select-models-dialog-form">
            <div className={SelectModelsDialogStyles.descriptionContainer}>
              <p className={SelectModelsDialogStyles.descriptionText}>{Messages.selectModelsDialogDescription()}</p>
              <TextLinkWidget
                className={learnMoreStyle}
                href={selectModelsLearnMoreLink}
                target="_blank"
                testId={'select-models-learn-more'}
              >
                {Messages.learnMore()}
              </TextLinkWidget>
            </div>
            <table className={`tb-form-table ${SelectModelsDialogStyles.formTable}`}>
              <tbody>
                <ModelLabelSection
                  htmlFor="modelQueryParsing"
                  label={Messages.selectModelsDialogQueryParsingLabel()}
                  tooltip={Messages.selectModelsDialogQueryParsingTooltip()}
                />
                {props.isOpenAIProvider ? (
                  <ModelDropdownSection
                    name="modelQueryParsing"
                    options={props.modelQueryParsingOptions}
                    value={formik.values.modelQueryParsing}
                    defaultModel={DEFAULT_MODELS.QUERY_PARSING}
                    isDefaultAvailable={props.isDefaultQueryParsingModelAvailable}
                    shouldShowAutomaticModelValidation={props.shouldShowAutomaticModelValidation}
                    testId="select-models-choose-modelQueryParsing"
                  />
                ) : (
                  <ModelTextFieldSection name="modelQueryParsing" testId="select-models-input-modelQueryParsing" />
                )}
                <ModelLabelSection
                  htmlFor="modelVizAndCalcGeneration"
                  label={Messages.selectModelsDialogVizAndCalcGenerationLabel()}
                  tooltip={Messages.selectModelsDialogVizAndCalcGenerationTooltip()}
                />
                {props.isOpenAIProvider ? (
                  <ModelDropdownSection
                    name="modelVizAndCalcGeneration"
                    options={props.modelVizAndCalcOptions}
                    value={formik.values.modelVizAndCalcGeneration}
                    defaultModel={DEFAULT_MODELS.VIZ_AND_CALC}
                    isDefaultAvailable={props.isDefaultVizAndCalcModelAvailable}
                    shouldShowAutomaticModelValidation={props.shouldShowAutomaticModelValidation}
                    testId="select-models-choose-modelVizAndCalcGeneration"
                  />
                ) : (
                  <ModelTextFieldSection name="modelVizAndCalcGeneration" testId="select-models-input-modelVizAndCalcGeneration" />
                )}
                <ModelLabelSection
                  htmlFor="modelEmbeddings"
                  label={Messages.selectModelsDialogEmbeddingsLabel()}
                  tooltip={Messages.selectModelsDialogEmbeddingsTooltip()}
                />
                {props.isOpenAIProvider ? (
                  <ModelDropdownSection
                    name="modelEmbeddings"
                    options={props.modelEmbeddingsOptions}
                    value={formik.values.modelEmbeddings}
                    defaultModel={DEFAULT_MODELS.EMBEDDINGS}
                    isDefaultAvailable={props.isDefaultEmbeddingsModelAvailable}
                    shouldShowAutomaticModelValidation={props.shouldShowAutomaticModelValidation}
                    testId="select-models-choose-modelEmbeddings"
                  />
                ) : (
                  <ModelTextFieldSection name="modelEmbeddings" testId="select-models-input-modelEmbeddings" />
                )}
              </tbody>
            </table>
            <div className="tb-dialog-actions">
              <ButtonWidget
                buttonType={ButtonType.Outline}
                testId="select-models-dialog-dismiss"
                onClick={() => props.onClose()}
                disabled={formik.isSubmitting}
                type="button"
              >
                {Messages.cancel()}
              </ButtonWidget>
              <ButtonWidget
                buttonType={ButtonType.Primary}
                testId="select-models-dialog-save-button"
                className="tb-margin-left-wide"
                disabled={isSubmitDisabled(formik, props)}
                type="submit"
              >
                {props.enablesFeatureOnApply ? Messages.selectModelsDialogTurnOn() : Messages.apply()}
              </ButtonWidget>
            </div>
          </form>
        )}
      </Formik>
    </DialogWidget>
  );
}
